import { CircleStopIcon, LoaderCircleIcon } from "lucide-react";
import { format, formatDistanceToNowStrict } from "date-fns";
import { zhCN } from "date-fns/locale";
import { Chip } from "@/components/badge/chip";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { DATE_TIME } from "@/lib/date";
import { cn } from "@/lib/utils";
import type { AgentSession, Prd } from "@/types/domain";

// Agent session (WED-312), as ccp hooks reported it. The state is the raw hook
// event name, shown as-is (no translation or per-state colors yet); only
// SessionEnd means something: an ended session gets a static icon, anything
// else spins. No staleness rule — the last event and its time stay put.
const AGENTS: Record<AgentSession["agent"], string> = {
  claude: "Claude",
  codex: "Codex",
};

export const agentSessionLabel = (s: AgentSession) =>
  `${AGENTS[s.agent] ?? s.agent} · ${s.state}`;

// `fixed`: list rows hide the chip on phones like the exec stage chip. No
// empty slot is reserved — most PRDs have no session, and the chip sits left
// of the exec stage slot so that column stays aligned.
export const AgentSessionChip = ({
  prd,
  className,
  fixed,
}: {
  prd: Pick<Prd, "agentSession">;
  className?: string;
  fixed?: boolean;
}) => {
  const session = prd.agentSession;
  if (!session) return null;
  const ended = !!session.endedAt;
  const Icon = ended ? CircleStopIcon : LoaderCircleIcon;
  const label = agentSessionLabel(session);
  const at = new Date(session.at);
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Chip
            aria-label={label}
            className={cn(
              "inline-flex items-center gap-1",
              fixed && "hidden shrink-0 md:inline-flex",
              className
            )}
          >
            <Icon
              aria-hidden="true"
              className={cn("h-3 w-3 shrink-0", !ended && "motion-safe:animate-spin")}
            />
            <span className="truncate">{label}</span>
            <span className="shrink-0 tabular-nums opacity-70">
              {formatDistanceToNowStrict(at, { addSuffix: true, locale: zhCN })}
            </span>
          </Chip>
        </TooltipTrigger>
        <TooltipContent>
          <p>{label}</p>
          {session.detail && <p>{session.detail}</p>}
          <p>
            {session.userName ?? session.userId} · session …
            {session.sessionId.slice(-6)}
          </p>
          <p>{format(at, DATE_TIME)}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};
